import { useMemo } from "react";
import { useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, ShoppingCart, MapPin, type LucideIcon } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useOrderHistory } from "@/hooks/useOrderHistory";
import type { CartResponse } from "@/hooks/useCart";

export type PrimaryActionKind = "track" | "review-order" | "cart" | "review";

export interface PrimaryAction {
  kind: PrimaryActionKind;
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  testId: string;
}

const COMPLETED_STATUSES = ["closed", "delivered", "completed"];
const CREATE_REVIEW_EVENT = "kudoz:open-create-review";

function emitCreateReview() {
  window.dispatchEvent(new CustomEvent(CREATE_REVIEW_EVENT));
}

interface CachedCart {
  restaurantId: string;
  itemCount: number;
  subtotal: number;
}

export function usePrimaryAction(): PrimaryAction {
  const [location, setLocation] = useLocation();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const { data: ordersResponse } = useOrderHistory(1, "", {
    enabled: isAuthenticated,
  });

  // Subscribe so that reviewed-id cache changes trigger a re-render and the
  // adaptive CTA flips off "Write Review" as soon as a review is posted.
  // Server returns `{ orderIds: string[], orders: { orderId, rating }[] }`.
  const { data: reviewedOrdersData } = useQuery<{
    orderIds: string[];
    orders: { orderId: string; rating: number }[];
  }>({
    queryKey: ["/api/user/reviewed-order-ids"],
    enabled: isAuthenticated,
  });

  // Subscribe to the cart query family so cart additions/clears update the
  // CTA immediately. We can't `useQuery` without knowing the restaurantId,
  // so we read all matching caches and use the query-cache subscription
  // signal from getQueryState by depending on the query-cache version
  // through `useQueryClient().getQueryCache().subscribe`. A lightweight
  // approach: read the snapshot inside useMemo and add a `cartVersion`
  // state that bumps on cache notifications.
  const cartSignature = useCartSignature(queryClient, true);

  return useMemo<PrimaryAction>(() => {
    const navigateAndCreateReview = () => {
      if (location !== "/") {
        setLocation("/");
        // After navigation, emit on next tick so Home can mount its listener.
        setTimeout(emitCreateReview, 0);
      } else {
        emitCreateReview();
      }
    };

    const defaultAction: PrimaryAction = {
      kind: "review",
      label: "Review",
      icon: Star,
      onClick: navigateAndCreateReview,
      testId: "nav-create-review",
    };

    const orders = isAuthenticated ? ordersResponse?.results ?? [] : [];

    // 0. Active in-flight order — highest priority. Shows "Track Order" so
    //    the user can monitor the order they just placed.
    const inFlightOrder = orders.find(
      (order) => order.status?.toLowerCase() === "in_progress"
    );
    if (inFlightOrder) {
      return {
        kind: "track",
        label: "Track Order",
        icon: MapPin,
        testId: "nav-track-order",
        onClick: () => setLocation(`/orders/${inFlightOrder.id}`),
      };
    }

    // 1. Pending review (completed but not yet reviewed) — must come BEFORE
    //    cart so a partner who's mid-order and has an unreviewed past order
    //    is nudged to write the review.
    const reviewedSet = new Set(
      (reviewedOrdersData?.orderIds ?? []).map(String)
    );
    const pendingReview = orders.find((order) => {
      const status = order.status?.toLowerCase() || "";
      if (!COMPLETED_STATUSES.includes(status)) return false;
      return !reviewedSet.has(String(order.id));
    });
    if (pendingReview) {
      return {
        kind: "review-order",
        label: "Write Review",
        icon: Star,
        testId: "nav-write-review",
        onClick: () => setLocation(`/profile?review=${pendingReview.id}`),
      };
    }

    // 3. Cart — pick the MOST RECENTLY UPDATED non-empty cart (by query
    //    cache `dataUpdatedAt`), not the biggest one. Re-evaluates whenever
    //    `cartSignature` bumps (see `useCartSignature` below).
    void cartSignature;
    const cartEntries = queryClient.getQueriesData<CartResponse>({
      queryKey: ["/api/restaurants"],
    });
    let activeCart: CachedCart | null = null;
    let activeCartUpdatedAt = 0;
    for (const [key, data] of cartEntries) {
      if (!Array.isArray(key) || key.length < 3) continue;
      if (key[0] !== "/api/restaurants" || key[2] !== "cart") continue;
      if (!data || !data.items || data.items.length === 0) continue;
      const itemCount =
        data.itemCount ??
        data.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
      if (itemCount <= 0) continue;
      const state = queryClient.getQueryState(key);
      const updatedAt = state?.dataUpdatedAt ?? 0;
      const candidate: CachedCart = {
        restaurantId: data.restaurantId || String(key[1] || ""),
        itemCount,
        subtotal: data.subtotal || 0,
      };
      if (!activeCart || updatedAt > activeCartUpdatedAt) {
        activeCart = candidate;
        activeCartUpdatedAt = updatedAt;
      }
    }

    // Cart suppressed on the matching restaurant's own page — its sticky
    // cart bar already owns the action; stacking two orange CTAs is the bug.
    if (activeCart) {
      const onOwnRestaurantPage =
        location === `/restaurant/${activeCart.restaurantId}` ||
        location.startsWith(`/restaurant/${activeCart.restaurantId}?`) ||
        location.startsWith(`/restaurant/${activeCart.restaurantId}/`);
      if (!onOwnRestaurantPage) {
        const formatted = activeCart.subtotal.toFixed(2);
        return {
          kind: "cart",
          label: `Cart · $${formatted}`,
          icon: ShoppingCart,
          testId: "nav-cart",
          onClick: () =>
            setLocation(`/restaurant/${activeCart!.restaurantId}?cart=open`),
        };
      }
    }

    return defaultAction;
  }, [
    isAuthenticated,
    ordersResponse,
    reviewedOrdersData,
    queryClient,
    location,
    setLocation,
    cartSignature,
  ]);
}

// Subscribes to the React Query cache and bumps a counter whenever ANY
// `["/api/restaurants", ..., "cart"]` query changes. This is the minimal
// reactive signal we need without inventing a per-restaurant useQuery list.
import { useEffect, useState } from "react";
import type { QueryClient } from "@tanstack/react-query";

function useCartSignature(queryClient: QueryClient, enabled: boolean): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const cache = queryClient.getQueryCache();
    const unsubscribe = cache.subscribe((event) => {
      const key = event.query.queryKey;
      if (
        Array.isArray(key) &&
        key[0] === "/api/restaurants" &&
        key[2] === "cart"
      ) {
        setVersion((v) => v + 1);
      }
    });
    return () => unsubscribe();
  }, [queryClient, enabled]);
  return version;
}
